import { CURRENCIES, DRIVER_LEDGER_KIND_LABELS, FINANCE_ENTRY_KINDS, type DriverLedgerEntryInput, type Currency } from '@chukua/common';
import { Alert, Button, Drawer, Form, Input, Select } from 'antd';
import { useState } from 'react';
import { parseMoneyAmount } from '../../../shared/policies/parse-money-amount';
import { toLedgerEntryInput, type LedgerEntryFormValues } from '../policies/ledger-entry-form';

const KIND_OPTIONS = FINANCE_ENTRY_KINDS.map((kind) => ({ value: kind, label: DRIVER_LEDGER_KIND_LABELS[kind] }));
const CURRENCY_OPTIONS = CURRENCIES.map((currency) => ({ value: currency, label: currency }));

interface DriverLedgerEntryDrawerProps {
  open: boolean;
  error: string | null;
  submitting: boolean;
  onRecord: (input: DriverLedgerEntryInput) => Promise<boolean>;
  onClose: () => void;
}

/** Finance records cash returned, a charge or a payroll deduction. The amount is a size; the kind decides which way the balance moves. */
export function DriverLedgerEntryDrawer({ open, error, submitting, onRecord, onClose }: DriverLedgerEntryDrawerProps) {
  const [form] = Form.useForm<LedgerEntryFormValues>();
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());

  async function submit(values: LedgerEntryFormValues) {
    const recorded = await onRecord(toLedgerEntryInput(values, idempotencyKey));
    if (!recorded) return;
    // A refusal keeps the key so a retry of the same request replays; only a recorded entry starts a new one.
    setIdempotencyKey(crypto.randomUUID());
    form.resetFields();
    onClose();
  }

  return (
    <Drawer open={open} onClose={onClose} title="Record ledger entry" placement="right" size={480} destroyOnClose>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<LedgerEntryFormValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={submit}
        disabled={submitting}
        initialValues={{ kind: 'cash_returned', currency: 'KES' satisfies Currency }}
      >
        <Form.Item name="kind" label="Kind" rules={[{ required: true, message: 'Choose a kind' }]}>
          <Select options={KIND_OPTIONS} />
        </Form.Item>
        <Form.Item
          name="amount"
          label="Amount"
          dependencies={['currency']}
          rules={[
            {
              validator: (_, value?: string) => {
                const check = parseMoneyAmount(value, form.getFieldValue('currency') as Currency);
                return check.ok ? Promise.resolve() : Promise.reject(new Error(check.reason));
              },
            },
          ]}
        >
          <Input
            inputMode="decimal"
            addonBefore={
              <Form.Item name="currency" noStyle>
                <Select aria-label="Currency" options={CURRENCY_OPTIONS} className="w-20" />
              </Form.Item>
            }
          />
        </Form.Item>
        <Form.Item name="note" label="What is it for?" rules={[{ required: true, whitespace: true, message: 'Say what this is for' }, { max: 200 }]}>
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" loading={submitting}>
            Record entry
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
